/**
 * ============================================================================
 * ADMIN USER MANAGEMENT PAGE
 * ============================================================================
 * Same structure and UX as Admin Transactions: hero, stats bento, filters,
 * user list, pagination.
 */

import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdmin } from '../../hooks/useAdmin';
import { useAdminStore } from '../../store';
import { useToast } from '../../providers';
import { useI18n } from '../../providers';
import AdminLayout from '../../components/admin/AdminLayout';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Select from '../../components/common/Select';
import Spinner from '../../components/common/Spinner';
import StatusBadge from '../../components/common/StatusBadge';
import Avatar from '../../components/common/Avatar';
import Modal from '../../components/common/Modal';
import Textarea from '../../components/common/Textarea';
import '../../styles/landing.css';
import '../../styles/animations.css';
import '../../styles/global.css';

/** Normalize backend user (snake_case) to frontend shape; map verification_level 0/1/2 to label */
function normalizeUser(u) {
  if (!u) return null;
  const level = u.verification_level ?? u.verificationLevel;
  const verificationLevel =
    level === 2 ? 'verified' : level === 1 ? 'pending' : 'unverified';
  return {
    ...u,
    name: u.full_name ?? u.name ?? u.fullName,
    fullName: u.full_name ?? u.fullName ?? u.name,
    photoUrl: u.profile_photo_url ?? u.photoUrl,
    createdAt: u.created_at ?? u.createdAt,
    verification_level: level,
    verificationLevel,
  };
}

const AdminUsers = () => {
  const navigate = useNavigate();
  const {
    listUsers,
    suspendUser,
    reactivateUser,
    banUser,
    verifyUser,
    loading,
    users,
  } = useAdmin();
  const { filters, setFilters, pagination, setPagination } = useAdminStore();
  const { showError, showSuccess } = useToast();
  const { formatDate, language } = useI18n();

  const [searchQuery, setSearchQuery] = useState(filters?.search || '');
  const [isLoading, setIsLoading] = useState(true);
  const [statsInView, setStatsInView] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState('');
  const [actionReason, setActionReason] = useState('');
  const statsRef = useRef(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        const result = await listUsers({
          ...filters,
          search: searchQuery || undefined,
          status: filters?.status || undefined,
          verificationLevel: filters?.verificationLevel || undefined,
          page: pagination?.page || 1,
          limit: pagination?.pageSize || 20,
        });
        if (result?.pagination) {
          setPagination({ ...pagination, ...result.pagination });
        }
      } catch (error) {
        showError('Failed to load users');
        console.error('Load users error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [
    filters,
    searchQuery,
    listUsers,
    showError,
    pagination?.page,
    pagination?.pageSize,
  ]);

  useEffect(() => {
    const el = statsRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => e.isIntersecting && setStatsInView(true));
      },
      { threshold: 0.2 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    setFilters({ ...filters, search: searchQuery });
    setPagination({ ...pagination, page: 1 });
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
    setPagination({ ...pagination, page: 1 });
  };

  const clearFilters = () => {
    setFilters({ search: null, status: null, verificationLevel: null });
    setSearchQuery('');
    setPagination({ ...pagination, page: 1 });
  };

  const handleActionClick = (user, action) => {
    setSelectedUser(user);
    setActionType(action);
    setActionReason('');
    setShowActionModal(true);
  };

  const handleAction = async () => {
    if (!selectedUser) return;

    try {
      switch (actionType) {
        case 'suspend':
          await suspendUser(selectedUser.id, { reason: actionReason });
          showSuccess(
            language === 'fr' ? 'Utilisateur suspendu' : 'User suspended'
          );
          break;
        case 'reactivate':
          await reactivateUser(selectedUser.id);
          showSuccess(
            language === 'fr' ? 'Utilisateur réactivé' : 'User reactivated'
          );
          break;
        case 'ban':
          await banUser(selectedUser.id, { reason: actionReason });
          showSuccess(
            language === 'fr' ? 'Utilisateur banni' : 'User banned'
          );
          break;
        case 'verify':
          await verifyUser(selectedUser.id, { verificationLevel: 2 });
          showSuccess(
            language === 'fr' ? 'Utilisateur vérifié' : 'User verified'
          );
          break;
      }
      setShowActionModal(false);
      setSelectedUser(null);
      await listUsers({
        ...filters,
        search: searchQuery || undefined,
        page: pagination?.page || 1,
        limit: pagination?.pageSize || 20,
      });
    } catch (error) {
      showError(
        error.message ||
          (language === 'fr' ? "Échec de l'action" : 'Failed to perform action')
      );
    }
  };

  const list = Array.isArray(users) ? users : [];
  const stats = {
    total: pagination?.total ?? list.length,
    active: list.filter((u) => u.status === 'active').length,
    suspended: list.filter((u) => u.status === 'suspended').length,
    banned: list.filter((u) => u.status === 'banned').length,
  };

  const statusOptions = [
    {
      value: '',
      label: language === 'fr' ? 'Tous les statuts' : 'All Statuses',
    },
    { value: 'active', label: language === 'fr' ? 'Actif' : 'Active' },
    {
      value: 'suspended',
      label: language === 'fr' ? 'Suspendu' : 'Suspended',
    },
    { value: 'banned', label: language === 'fr' ? 'Banni' : 'Banned' },
  ];

  const totalFromPagination = pagination?.total ?? list.length;
  const showPagination =
    totalFromPagination > (pagination?.pageSize ?? 20);

  if (isLoading || loading) {
    return (
      <AdminLayout hideFooter>
        <div className="transactions-wt transactions-wt-loading">
          <div className="dashboard-wt-hero-inner">
            <Spinner size="lg" />
            <p className="mt-4 text-[#a3a3a3]">
              {language === 'fr'
                ? 'Chargement des utilisateurs...'
                : 'Loading users...'}
            </p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="transactions-wt dashboard-wt">
        {/* Hero */}
        <section className="dashboard-wt-hero">
          <div className="dashboard-wt-hero-bg" aria-hidden="true" />
          <div className="dashboard-wt-hero-grid" aria-hidden="true" />
          <div className="dashboard-wt-hero-noise" aria-hidden="true" />
          <div
            className="dashboard-wt-hero-orb dashboard-wt-hero-orb-1"
            aria-hidden="true"
          />
          <div
            className="dashboard-wt-hero-orb dashboard-wt-hero-orb-2"
            aria-hidden="true"
          />

          <div className="dashboard-wt-hero-inner">
            <h1 className="dashboard-wt-hero-title">
              👥{' '}
              {language === 'fr'
                ? 'Gestion des utilisateurs'
                : 'User Management'}
            </h1>
            <p className="dashboard-wt-hero-sub">
              {language === 'fr'
                ? 'Gérez les utilisateurs, vérifications et statuts'
                : 'Manage users, verifications, and statuses'}
            </p>
            <button
              type="button"
              className="dashboard-wt-btn-primary dashboard-wt-btn-large mt-6"
              onClick={() => navigate('/admin/dashboard')}
            >
              <span className="dashboard-wt-btn-icon">←</span>
              {language === 'fr'
                ? 'Retour au tableau de bord'
                : 'Back to Dashboard'}
            </button>
          </div>
        </section>

        {/* Stats - Bento */}
        <section className="dashboard-wt-section">
          <div className="dashboard-wt-section-inner">
            <div
              className={`dashboard-wt-stats ${statsInView ? 'is-inview' : ''}`}
              ref={statsRef}
            >
              {[
                {
                  icon: '👥',
                  label: language === 'fr' ? 'Total' : 'Total',
                  value: stats.total,
                },
                {
                  icon: '✅',
                  label: language === 'fr' ? 'Actifs' : 'Active',
                  value: stats.active,
                },
                {
                  icon: '⏸️',
                  label: language === 'fr' ? 'Suspendus' : 'Suspended',
                  value: stats.suspended,
                },
                {
                  icon: '🚫',
                  label: language === 'fr' ? 'Bannis' : 'Banned',
                  value: stats.banned,
                },
              ].map((stat, i) => (
                <div
                  key={i}
                  className="dashboard-wt-stat-card"
                  style={{ animationDelay: `${i * 0.1}s` }}
                >
                  <div className="dashboard-wt-stat-icon">
                    <span>{stat.icon}</span>
                  </div>
                  <div className="dashboard-wt-stat-content">
                    <div className="dashboard-wt-stat-value">{stat.value}</div>
                    <div className="dashboard-wt-stat-label">{stat.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Filters */}
        <section className="dashboard-wt-section dashboard-wt-section-alt">
          <div className="dashboard-wt-section-inner">
            <div className="transactions-wt-filters dashboard-wt-card">
              <div className="dashboard-wt-card-header">
                <h2 className="dashboard-wt-card-title">
                  {language === 'fr' ? 'Filtres' : 'Filters'}
                </h2>
              </div>
              <div className="dashboard-wt-card-content">
                <form
                  onSubmit={handleSearch}
                  className="transactions-wt-form"
                >
                  <div className="transactions-wt-form-row">
                    <div className="transactions-wt-form-search">
                      <Input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder={
                          language === 'fr'
                            ? 'Nom, téléphone, email, ID...'
                            : 'Name, phone, email, ID...'
                        }
                        className="transactions-wt-input"
                      />
                    </div>
                    <div className="transactions-wt-form-select">
                      <Select
                        value={filters?.status || ''}
                        onChange={(e) =>
                          handleFilterChange(
                            'status',
                            e.target.value || null
                          )
                        }
                        options={statusOptions}
                      />
                    </div>
                    <button type="submit" className="dashboard-wt-btn-primary">
                      {language === 'fr' ? 'Rechercher' : 'Search'}
                    </button>
                    {(filters?.search || filters?.status) && (
                      <button
                        type="button"
                        className="dashboard-wt-btn-secondary"
                        onClick={clearFilters}
                      >
                        {language === 'fr' ? 'Effacer' : 'Clear'}
                      </button>
                    )}
                  </div>
                </form>
              </div>
            </div>

            {/* List or empty */}
            {list.length === 0 ? (
              <div className="dashboard-wt-card dashboard-wt-empty">
                <div className="dashboard-wt-empty-icon">👥</div>
                <h3 className="dashboard-wt-empty-title">
                  {language === 'fr'
                    ? 'Aucun utilisateur'
                    : 'No users found'}
                </h3>
                <p className="dashboard-wt-empty-desc">
                  {language === 'fr'
                    ? 'Aucun utilisateur ne correspond à vos filtres'
                    : 'No users match your filters'}
                </p>
                <button
                  type="button"
                  className="dashboard-wt-btn-secondary dashboard-wt-btn-large"
                  onClick={clearFilters}
                >
                  {language === 'fr'
                    ? 'Effacer les filtres'
                    : 'Clear Filters'}
                </button>
              </div>
            ) : (
              <>
                <div className="dashboard-wt-transactions transactions-wt-list">
                  {list.map((user, index) => {
                    const u = normalizeUser(user);
                    return (
                      <div
                        key={user.id}
                        className="dashboard-wt-transaction-wrapper dashboard-wt-card"
                        style={{
                          animationDelay: `${index * 0.05}s`,
                        }}
                      >
                        <div className="dashboard-wt-card-content">
                          <div className="flex flex-wrap items-center gap-4">
                            <Avatar
                              name={u.name || u.fullName || 'U'}
                              src={u.photoUrl}
                              size="md"
                              variant="circle"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-wrap items-center gap-2 mb-1">
                                <h3 className="font-bold text-[#e5e5e5]">
                                  {u.name || u.fullName || 'N/A'}
                                </h3>
                                <StatusBadge
                                  variant={
                                    u.status === 'active'
                                      ? 'success'
                                      : u.status === 'suspended'
                                      ? 'warning'
                                      : u.status === 'banned'
                                      ? 'error'
                                      : 'default'
                                  }
                                  size="sm"
                                >
                                  {(u.status || '').toUpperCase()}
                                </StatusBadge>
                              </div>
                              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-[#a3a3a3]">
                                <span>{u.phone || 'N/A'}</span>
                                {u.email && (
                                  <>
                                    <span>•</span>
                                    <span>{u.email}</span>
                                  </>
                                )}
                                <span>•</span>
                                <span>
                                  {u.createdAt && formatDate(u.createdAt)}
                                </span>
                              </div>
                              <div className="mt-2">
                                <StatusBadge
                                  variant={
                                    u.verificationLevel === 'verified'
                                      ? 'success'
                                      : u.verificationLevel === 'pending'
                                      ? 'warning'
                                      : 'default'
                                  }
                                  size="sm"
                                >
                                  {u.verificationLevel === 'verified'
                                    ? language === 'fr'
                                      ? 'Vérifié (Niveau 2)'
                                      : 'Verified (Level 2)'
                                    : u.verificationLevel === 'pending'
                                    ? language === 'fr'
                                      ? 'En attente (Niveau 1)'
                                      : 'Pending (Level 1)'
                                    : language === 'fr'
                                    ? 'Non vérifié (Niveau 0)'
                                    : 'Unverified (Level 0)'}
                                </StatusBadge>
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-2 flex-shrink-0">
                              <button
                                type="button"
                                className="dashboard-wt-btn-secondary"
                                onClick={() =>
                                  navigate(`/admin/users/${user.id}`)
                                }
                              >
                                {language === 'fr' ? 'Voir' : 'View'}
                              </button>
                              {u.status === 'active' && (
                                <button
                                  type="button"
                                  className="dashboard-wt-btn-secondary border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10"
                                  onClick={() =>
                                    handleActionClick(user, 'suspend')
                                  }
                                >
                                  {language === 'fr' ? 'Suspendre' : 'Suspend'}
                                </button>
                              )}
                              {u.status === 'suspended' && (
                                <button
                                  type="button"
                                  className="dashboard-wt-btn-secondary border-green-500/50 text-green-400 hover:bg-green-500/10"
                                  onClick={() =>
                                    handleActionClick(user, 'reactivate')
                                  }
                                >
                                  {language === 'fr'
                                    ? 'Réactiver'
                                    : 'Reactivate'}
                                </button>
                              )}
                              {u.status === 'active' && (
                                <button
                                  type="button"
                                  className="dashboard-wt-btn-secondary border-red-500/50 text-red-400 hover:bg-red-500/10"
                                  onClick={() =>
                                    handleActionClick(user, 'ban')
                                  }
                                >
                                  {language === 'fr' ? 'Bannir' : 'Ban'}
                                </button>
                              )}
                              {u.verificationLevel !== 'verified' && (
                                <button
                                  type="button"
                                  className="dashboard-wt-btn-primary"
                                  onClick={() =>
                                    handleActionClick(user, 'verify')
                                  }
                                >
                                  {language === 'fr' ? 'Vérifier' : 'Verify'} ✓
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {showPagination && (
                  <div className="transactions-wt-pagination dashboard-wt-card">
                    <div className="transactions-wt-pagination-inner">
                      <span className="transactions-wt-pagination-text">
                        {language === 'fr' ? 'Affichage' : 'Showing'}{' '}
                        {(pagination.page - 1) *
                          (pagination.pageSize || 20) +
                          1}{' '}
                        {language === 'fr' ? 'à' : 'to'}{' '}
                        {Math.min(
                          pagination.page * (pagination.pageSize || 20),
                          totalFromPagination
                        )}{' '}
                        {language === 'fr' ? 'sur' : 'of'}{' '}
                        {totalFromPagination}
                      </span>
                      <div className="transactions-wt-pagination-btns">
                        <button
                          type="button"
                          className="dashboard-wt-btn-secondary"
                          onClick={() =>
                            setPagination({
                              ...pagination,
                              page: pagination.page - 1,
                            })
                          }
                          disabled={pagination.page <= 1}
                        >
                          {language === 'fr' ? 'Précédent' : 'Previous'}
                        </button>
                        <button
                          type="button"
                          className="dashboard-wt-btn-secondary"
                          onClick={() =>
                            setPagination({
                              ...pagination,
                              page: pagination.page + 1,
                            })
                          }
                          disabled={
                            pagination.page * (pagination.pageSize || 20) >=
                            totalFromPagination
                          }
                        >
                          {language === 'fr' ? 'Suivant' : 'Next'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </div>

      {/* Action Modal */}
      <Modal
        isOpen={showActionModal}
        onClose={() => {
          setShowActionModal(false);
          setSelectedUser(null);
          setActionReason('');
        }}
        title={
          actionType === 'suspend'
            ? language === 'fr'
              ? "Suspendre l'utilisateur"
              : 'Suspend User'
            : actionType === 'ban'
            ? language === 'fr'
              ? "Bannir l'utilisateur"
              : 'Ban User'
            : actionType === 'verify'
            ? language === 'fr'
              ? "Vérifier l'utilisateur"
              : 'Verify User'
            : language === 'fr'
            ? "Réactiver l'utilisateur"
            : 'Reactivate User'
        }
      >
        <div className="space-y-4">
          {selectedUser && (
            <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-xl">
              <Avatar
                name={
                  normalizeUser(selectedUser).name ||
                  normalizeUser(selectedUser).fullName ||
                  'U'
                }
                size="md"
                variant="circle"
              />
              <div>
                <p className="font-bold text-gray-900 dark:text-white">
                  {normalizeUser(selectedUser).name ||
                    normalizeUser(selectedUser).fullName ||
                    'N/A'}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {selectedUser.email || selectedUser.phone || 'N/A'}
                </p>
              </div>
            </div>
          )}
          {(actionType === 'suspend' || actionType === 'ban') && (
            <Textarea
              label={
                language === 'fr' ? 'Raison (requis)' : 'Reason (required)'
              }
              value={actionReason}
              onChange={(e) => setActionReason(e.target.value)}
              placeholder={
                language === 'fr'
                  ? "Expliquez pourquoi cette action est nécessaire..."
                  : 'Explain why this action is necessary...'
              }
              rows={4}
              required
            />
          )}
          <div className="flex gap-3">
            <Button
              variant="outline"
              fullWidth
              onClick={() => {
                setShowActionModal(false);
                setSelectedUser(null);
                setActionReason('');
              }}
            >
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button
              variant="gradient"
              fullWidth
              onClick={handleAction}
              disabled={
                (actionType === 'suspend' || actionType === 'ban') &&
                !actionReason.trim()
              }
              className={
                actionType === 'ban'
                  ? 'bg-gradient-to-r from-red-600 to-pink-600'
                  : actionType === 'suspend'
                  ? 'bg-gradient-to-r from-yellow-600 to-orange-600'
                  : ''
              }
            >
              {actionType === 'suspend'
                ? language === 'fr'
                  ? 'Suspendre'
                  : 'Suspend'
                : actionType === 'ban'
                ? language === 'fr'
                  ? 'Bannir'
                  : 'Ban'
                : actionType === 'verify'
                ? language === 'fr'
                  ? 'Vérifier'
                  : 'Verify'
                : language === 'fr'
                ? 'Réactiver'
                : 'Reactivate'}
            </Button>
          </div>
        </div>
      </Modal>
    </AdminLayout>
  );
};

export default AdminUsers;
